// src/pages/QbusAutomations/index.jsx
// Main page for managing Qbus automation flows per location
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Alert,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as AutomationIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { qbusApi } from '../../services/apiClient';
import { locationsApi } from '../../services/apiClient';
import { usePermissions } from '../../contexts/PermissionContext';
import AutomationList from './AutomationList';
import AutomationEditor from './AutomationEditor';

const QbusAutomations = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission('admin') || hasPermission('qbus.admin');

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(true);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);

  const selectedLocationId = selectedSite?.id || '';

  // Fetch sites only
  useEffect(() => {
    (async () => {
      try {
        const response = await locationsApi.getSites();
        const data = response.data;
        setSites(Array.isArray(data) ? data : (data?.sites || data?.data || []));
      } catch (error) {
        console.error('Error fetching sites:', error);
      } finally {
        setSitesLoading(false);
      }
    })();
  }, []);

  // Fetch automations when location changes
  const fetchAutomations = useCallback(async () => {
    if (!selectedLocationId) return;
    setLoading(true);
    try {
      const response = await qbusApi.getAutomations(selectedLocationId);
      setAutomations(response.data.automations || []);
    } catch (error) {
      console.error('Error fetching automations:', error);
      enqueueSnackbar('Failed to load automations', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, enqueueSnackbar]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  // Handlers
  const handleCreateNew = () => {
    setEditingAutomation(null);
    setEditorOpen(true);
  };

  const handleEdit = async (automationId) => {
    try {
      const response = await qbusApi.getAutomation(automationId);
      setEditingAutomation(response.data.automation);
      setEditorOpen(true);
    } catch (error) {
      enqueueSnackbar('Failed to load automation', { variant: 'error' });
    }
  };

  const handleToggle = async (automationId) => {
    try {
      await qbusApi.toggleAutomation(automationId);
      fetchAutomations();
    } catch (error) {
      enqueueSnackbar('Failed to toggle automation', { variant: 'error' });
    }
  };

  const handleDelete = async (automationId) => {
    try {
      await qbusApi.deleteAutomation(automationId);
      enqueueSnackbar('Automation deleted', { variant: 'success' });
      fetchAutomations();
    } catch (error) {
      enqueueSnackbar('Failed to delete automation', { variant: 'error' });
    }
  };

  const handleTest = async (automationId) => {
    try {
      const response = await qbusApi.testAutomation(automationId);
      const execution = response.data.execution;
      if (execution.status === 'completed') {
        enqueueSnackbar('Test run completed successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(`Test run ${execution.status}: ${execution.error_message || ''}`, { variant: 'warning' });
      }
    } catch (error) {
      enqueueSnackbar('Test run failed', { variant: 'error' });
    }
  };

  const handleEditorSave = async (data) => {
    try {
      if (editingAutomation) {
        await qbusApi.updateAutomation(editingAutomation.id, data);
        enqueueSnackbar('Automation updated', { variant: 'success' });
      } else {
        await qbusApi.createAutomation(selectedLocationId, data);
        enqueueSnackbar('Automation created', { variant: 'success' });
      }
      setEditorOpen(false);
      setEditingAutomation(null);
      fetchAutomations();
    } catch (error) {
      const validationErrors = error.response?.data?.validationErrors;
      if (validationErrors) {
        enqueueSnackbar(`Validation: ${validationErrors.join(', ')}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to save automation', { variant: 'error' });
      }
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingAutomation(null);
  };

  // Import: open editor with pre-loaded flow from JSON
  const handleImport = (data) => {
    setEditingAutomation({
      name: data.name || 'Imported Automation',
      description: data.description || '',
      flow_definition: data.flow_definition,
    });
    setEditorOpen(true);
  };

  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        You need admin permissions to access automation management.
      </Alert>
    );
  }

  // Full-screen editor mode
  if (editorOpen) {
    return (
      <AutomationEditor
        automation={editingAutomation}
        locationId={selectedLocationId}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutomationIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5">Qbus Automations</Typography>
            <Typography variant="body2" color="text.secondary">
              Define automation flows that trigger when outputs change state
            </Typography>
          </Box>
        </Box>
        {selectedLocationId && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            New Automation
          </Button>
        )}
      </Box>

      {/* Site selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          options={sites}
          getOptionLabel={(option) => option.friendlyName || option.name || `Site ${option.id}`}
          value={selectedSite}
          onChange={(_e, value) => setSelectedSite(value)}
          loading={sitesLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label="Select Site" size="small" />
          )}
        />
      </Paper>

      {/* Content */}
      {!selectedLocationId ? (
        <Alert severity="info">
          Select a site to view and manage its automations.
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AutomationList
          automations={automations}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onTest={handleTest}
          onImport={handleImport}
        />
      )}
    </Box>
  );
};

export default QbusAutomations;
